import api from './api';

export interface BankAccountInfo {
  bank_name?: string;
  account_name?: string;
  account_number?: string;
  iban?: string;
  swift_code?: string;
  branch_name?: string;
  branch_code?: string;
  currency?: string;
}

export interface PartnershipRevenueSharing {
  id?: number;
  country_code: string;
  country_name: string;
  revenue_share_percentage: number;
  partner_name?: string;
  partner_email?: string;
  partner_phone?: string;
  bank_account_details?: BankAccountInfo;
  is_active: boolean;
  notes?: string;
  created_at?: string;
  updated_at?: string;
  created_by?: number;
  updated_by?: number;
}

export interface RevenueSharingTransaction {
  id?: number;
  country_code: string;
  garage_id: number;
  subscription_id?: number;
  payment_id?: number;
  transaction_type: 'subscription' | 'commission' | 'other';
  gross_amount: number;
  share_percentage: number;
  share_amount: number;
  net_amount: number;
  status: 'pending' | 'processed' | 'paid' | 'cancelled';
  processed_at?: string;
  paid_at?: string;
  payment_reference?: string;
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

export interface RevenueSharingSummary {
  country_code: string;
  country_name: string;
  partner_name?: string;
  total_garages: number;
  total_transactions: number;
  pending_transactions: number;
  paid_transactions: number;
  total_gross_amount: number;
  total_share_amount: number;
  pending_share_amount: number;
  paid_share_amount: number;
  last_transaction_date?: string;
}

export interface TransactionFilters {
  country_code?: string;
  garage_id?: number;
  status?: string;
  start_date?: string;
  end_date?: string;
}

// Revenue Sharing Configuration APIs
export const revenueSharingAPI = {
  // Get all revenue sharing configurations
  getAllConfigs: async (): Promise<PartnershipRevenueSharing[]> => {
    const response = await api.get('/admin/revenue-sharing');
    return response.data;
  },

  // Get revenue sharing config by country
  getConfigByCountry: async (countryCode: string): Promise<PartnershipRevenueSharing> => {
    const response = await api.get(`/admin/revenue-sharing/${countryCode}`);
    return response.data;
  },

  // Create new revenue sharing configuration
  createConfig: async (config: PartnershipRevenueSharing): Promise<PartnershipRevenueSharing> => {
    const response = await api.post('/admin/revenue-sharing', config);
    return response.data;
  },

  // Update revenue sharing configuration
  updateConfig: async (countryCode: string, config: Partial<PartnershipRevenueSharing>): Promise<void> => {
    await api.put(`/admin/revenue-sharing/${countryCode}`, config);
  },

  // Delete revenue sharing configuration
  deleteConfig: async (countryCode: string): Promise<void> => {
    await api.delete(`/admin/revenue-sharing/${countryCode}`);
  },

  // Get revenue sharing transactions
  getTransactions: async (filters?: TransactionFilters): Promise<RevenueSharingTransaction[]> => {
    const response = await api.get('/admin/revenue-sharing/transactions', { params: filters });
    return response.data;
  },

  // Create revenue sharing transaction
  createTransaction: async (transaction: RevenueSharingTransaction): Promise<RevenueSharingTransaction> => {
    const response = await api.post('/admin/revenue-sharing/transactions', transaction);
    return response.data;
  },

  // Update transaction status
  updateTransactionStatus: async (transactionId: number, status: string, paymentReference?: string): Promise<void> => {
    await api.put(`/admin/revenue-sharing/transactions/${transactionId}/status`, {
      status,
      payment_reference: paymentReference
    });
  },

  // Batch update transaction status
  batchUpdateTransactionStatus: async (transactionIds: number[], status: string, paymentReference?: string): Promise<void> => {
    await api.post('/admin/revenue-sharing/transactions/batch-update', {
      transaction_ids: transactionIds,
      status,
      payment_reference: paymentReference
    });
  },

  // Get revenue sharing summary
  getSummary: async (): Promise<RevenueSharingSummary[]> => {
    const response = await api.get('/admin/revenue-sharing/summary');
    return response.data;
  },

  // Get pending transactions total for a country
  getPendingTotal: async (countryCode: string): Promise<{ country_code: string; pending_total: number }> => {
    const response = await api.get(`/admin/revenue-sharing/${countryCode}/pending-total`);
    return response.data;
  }
};